import { queryOptions } from '@tanstack/react-query'
import { exchangeFirebaseToken } from '../api/token.service.ts'

/**
 * Query options for company token with smart caching
 *
 * This manages the company token as TanStack Query cached data with:
 * - Dynamic staleTime based on token expiry
 * - Automatic cache separation by companyId
 * - No unnecessary refetching on mount or window focus
 */
export const companyTokenQueryOptions = (
  companyId: string,
  getFirebaseToken: () => Promise<string>
) =>
  queryOptions({
    queryKey: ['_global', 'company-token', companyId],
    queryFn: async () => {
      const firebaseToken = await getFirebaseToken()
      return await exchangeFirebaseToken(companyId, firebaseToken)
    },
    staleTime: query => {
      // Calculate stale time based on token expiry
      const data = query.state.data
      if (!data?.expiresAt) return 0

      const now = Date.now()
      const expiresAt = data.expiresAt.getTime()
      const buffer = 60 * 1000 // 1 minute buffer
      const timeUntilStale = expiresAt - now - buffer

      return Math.max(0, timeUntilStale)
    },
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes after last use
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data exists
  })
